import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { IThunkAPI } from '@/store/types';
import { permissionAdminApi } from '@/api/admin/permission';
import type { IPermission, IPermissionLevel, IPermissionState } from '../types/types';
import type { RootState } from '@/store';

const initialState: IPermissionState = {
  permissionList: [],
  permissionByClientList: [],
  permissionLevelList: [],
};

export const getPermissionList = createAsyncThunk<Array<IPermission>, undefined, IThunkAPI>(
  'get/permission/list',
  async (_, thunkAPI) => {
    try {
      const response = await permissionAdminApi.getPermissionList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getPermissionByClientIdList = createAsyncThunk<Array<IPermission>, number, IThunkAPI>(
  'get/permission/client/clientId/list',
  async (clientId, thunkAPI) => {
    try {
      const response = await permissionAdminApi.getPermissionByClientIdList(clientId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getPermissionLevelList = createAsyncThunk<Array<IPermissionLevel>, undefined, IThunkAPI>(
  'get/permissionlevel/list',
  async (_, thunkAPI) => {
    try {
      const response = await permissionAdminApi.getPermissionLevelList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const savePermission = createAsyncThunk<unknown, IPermission, IThunkAPI>(
  'post/admin/permission/create',
  async (permission, thunkAPI) => {
    try {
      const response = await permissionAdminApi.savePermission(permission);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const permissionAdminSlice = createSlice({
  name: 'permissionAdmin',
  initialState,
  reducers: {
    clearPermissions(state) {
      state.permissionList = [];
    },
    clearPermissionByClients(state) {
      state.permissionByClientList = [];
    },
    clearPermissionLevels(state) {
      state.permissionLevelList = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getPermissionList.fulfilled, (state, action) => {
        state.permissionList = action.payload;
      })
      .addCase(getPermissionLevelList.fulfilled, (state, action) => {
        state.permissionLevelList = action.payload;
      })
      .addCase(getPermissionByClientIdList.fulfilled, (state, action) => {
        state.permissionByClientList = action.payload;
      });
  },
});

// actions
export const { clearPermissions, clearPermissionLevels, clearPermissionByClients } = permissionAdminSlice.actions;

// selectors
export const permissionList = (state: RootState): IPermissionState['permissionList'] =>
  state.permissionAdmin.permissionList;
export const permissionByClientList = (state: RootState): IPermissionState['permissionByClientList'] =>
  state.permissionAdmin.permissionByClientList;

export const permissionLevelList = (state: RootState): IPermissionState['permissionLevelList'] =>
  state.permissionAdmin.permissionLevelList;

export default permissionAdminSlice.reducer;
